import { useEffect, useCallback, useRef } from "react";
import { QueryClientProvider, useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { getQueryClient } from "~/widgets/lib/query-client";
import { fetchReviewsSummary, fetchReviewsList } from "./api";
import { ReviewCard } from "./ReviewCard";
import type { ReviewsScope, ListingType, Review } from "./types";

export type ReviewsDialogOverlayProps = {
  businessSlug: string;
  apiUrl?: string;
  scope?: ReviewsScope;
  listingId?: string;
  listingType?: ListingType;
};

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function StarIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      className={`w-5 h-5 ${filled ? "text-primary fill-primary" : "text-muted-foreground/30 fill-muted-foreground/30"}`}
      viewBox="0 0 20 20"
      fill="currentColor"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function StarRatingSummary({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <StarIcon key={star} filled={star <= Math.round(rating)} />
      ))}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex justify-center py-8">
      <div className="w-8 h-8 border-2 border-muted rounded-full animate-spin border-t-primary" />
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="bg-card border border-border rounded-xl p-4 animate-pulse">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-muted rounded-full" />
            <div className="flex-1">
              <div className="h-4 bg-muted rounded w-24 mb-2" />
              <div className="h-3 bg-muted rounded w-16" />
            </div>
          </div>
          <div className="flex gap-1 mb-3">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="w-4 h-4 bg-muted rounded" />
            ))}
          </div>
          <div className="space-y-2">
            <div className="h-3 bg-muted rounded w-full" />
            <div className="h-3 bg-muted rounded w-3/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

function ReviewsDialogContent({
  businessSlug,
  apiUrl,
  scope = "business",
  listingId,
  listingType = "course",
}: ReviewsDialogOverlayProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Handle close - dispatch custom event and remove element
  const handleClose = useCallback(() => {
    // Dispatch close event
    const event = new CustomEvent("baluu:reviews-close", {
      bubbles: true,
      composed: true,
    });
    document.dispatchEvent(event);

    // Remove this overlay from the DOM
    const overlays = document.querySelectorAll("baluu-reviews-dialog");
    overlays.forEach((overlay) => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    });
  }, []);

  // Fetch summary
  const { data: summary, isLoading: isSummaryLoading } = useQuery({
    queryKey: ["reviews-summary", businessSlug, scope, listingId, listingType],
    queryFn: () => fetchReviewsSummary({ apiUrl, businessSlug, scope, listingId, listingType }),
  });

  // Fetch reviews with infinite scroll
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery({
    queryKey: ["reviews-list", businessSlug, scope, listingId, listingType],
    queryFn: ({ pageParam = 1 }) =>
      fetchReviewsList({
        apiUrl,
        businessSlug,
        scope,
        listingId,
        listingType,
        page: pageParam,
        limit: 10,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.page + 1 : undefined,
    initialPageParam: 1,
  });

  // Handle scroll for infinite loading
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetchingNextPage || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight * 1.5) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(e: KeyboardEvent) {
      if (e.key === "Escape") handleClose();
    }
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [handleClose]);

  // Prevent body scroll when dialog is open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  const allReviews: Review[] = data?.pages.flatMap((page) => page.reviews) ?? [];

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div 
        className="bg-background border border-border rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl overflow-hidden"
        role="dialog" 
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div className="flex-1 min-w-0">
            {isSummaryLoading ? (
              <div className="animate-pulse">
                <div className="h-6 bg-muted rounded w-48 mb-2" />
                <div className="h-4 bg-muted rounded w-24" />
              </div>
            ) : (
              <>
                <h2 className="text-lg font-semibold text-foreground truncate">
                  {summary?.listingTitle || summary?.businessName || "Customer Reviews"}
                </h2>
                {summary && (
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {summary.reviewCount} {summary.reviewCount === 1 ? "review" : "reviews"}
                  </p>
                )}
              </>
            )}
          </div>
          <button
            type="button"
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={handleClose}
            aria-label="Close reviews"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Summary Section */}
        {!isSummaryLoading && summary && summary.reviewCount > 0 && (
          <div className="flex items-center gap-4 p-5 bg-muted/50 border-b border-border">
            <div className="flex items-baseline gap-1">
              <span className="text-4xl font-bold text-foreground">
                {(summary.averageRating / 2).toFixed(1)}
              </span>
              <span className="text-lg text-muted-foreground">/5</span>
            </div>
            <div className="flex flex-col gap-1">
              <StarRatingSummary rating={summary.averageRating / 2} />
              <span className="text-xs text-muted-foreground">
                Based on {summary.reviewCount} {summary.reviewCount === 1 ? "review" : "reviews"}
              </span>
            </div>
          </div>
        )}

        {/* Reviews List */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto p-5"
          onScroll={handleScroll}
        >
          {isLoading ? (
            <LoadingSkeleton />
          ) : allReviews.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <svg className="w-8 h-8 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
              </div>
              <p className="text-muted-foreground font-medium">No reviews yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Be the first to leave a review!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {allReviews.map((review) => (
                <ReviewCard key={review.id} review={review} />
              ))}
              {isFetchingNextPage && <LoadingSpinner />}
              {!hasNextPage && allReviews.length > 0 && (
                <p className="text-center text-xs text-muted-foreground py-2">
                  You've seen all reviews
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function ReviewsDialogOverlay(props: ReviewsDialogOverlayProps) {
  const queryClient = getQueryClient();
  
  return (
    <QueryClientProvider client={queryClient}>
      <ReviewsDialogContent {...props} />
    </QueryClientProvider>
  );
}

